
import { useState, useEffect, FC, useMemo } from 'react';
import { UserData, UserDataContext } from './userDataContext';
import { ReactSimpleComponentProps } from '@/custom-types';

const LOCAL_STORAGE_KEY = 'userData';

/**
 * UserDataContextProvider component provides user data context to its children.
 * It manages user data state and persists it in local storage.
 *
 * @param props - The props for the component.
 * @param props.children - The child components that will have access to the user data context.
 *
 * @returns The UserDataContext.Provider component with user data and actions.
 *
 * @component
 *
 * @example
 * // Wrap your application with UserDataContextProvider to provide user data context
 * <UserDataContextProvider>
 *   <App />
 * </UserDataContextProvider>
 */
export const UserDataContextProvider: FC<ReactSimpleComponentProps> = ({ children }) => {
  const [userData, setUserDataState] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true)
  
  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    
    if (storedUserData) setUserDataState(JSON.parse(storedUserData));

    setIsLoading(false)
  }, []);

  const actions = useMemo(() => ({
    setUserData: (data?: UserData) => {
      if (data) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
        setUserDataState(data);
      }
    },
    getUserData: (): UserData | null => {
      // Se lee directamente de localStorage para asegurar que la función sea estable
      // y no dependa del estado del componente.
      const storedUserData = localStorage.getItem(LOCAL_STORAGE_KEY);
      return storedUserData ? JSON.parse(storedUserData) : null;
    },
    clearUserData: () => {
      localStorage.removeItem(LOCAL_STORAGE_KEY)
      setUserDataState(null);
    },
  }), [])

  return (
    <UserDataContext.Provider value={{ ...actions, isLoading, userData }}>
      {children}
    </UserDataContext.Provider>
  );
};